import { useId, useRef } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import { formatNumber } from '@shared/format';
import { TrashIcon } from '@shared/ui/icons';
import { PlusIcon } from '@shared/ui/icons/nav';

import { CONTROL_CLASS, IDENTIFIER_CLASS } from '../_shared/components';

import { copy } from './copy';
import type { ContactValues } from './forms.schema';

/**
 * The social handles — a set the operator fills, so adding and removing rows is
 * how the set is managed.
 *
 * **The row is a free label plus a URL, never a choice from a list.** The
 * catalog says «configured social handles» and declares no platform vocabulary
 * anywhere, so a `<select>` here would be this design inventing a set. They
 * render as text on the public page and not as platform logos, for the same
 * reason: a logo is a third-party mark outside the icon set, and drawing one
 * asserts a relationship nobody established.
 *
 * **Removing a row is not the destructive action the design system governs.**
 * That rule covers a persisted record in a table and demands a row menu and a
 * named confirmation. A repeater row is an unsaved form control: nothing is
 * destroyed before the save, and «أعد الحقول» restores it. So the remove sits
 * inline as a ghost icon button whose accessible name states the handle it
 * removes, and no dialog stands between the operator and a field they are still
 * filling.
 */
export function ContactHandleRepeater({
  form,
  announce,
}: {
  form: UseFormReturn<ContactValues>;
  announce: (message: string) => void;
}) {
  const fieldPrefix = useId();
  const container = useRef<HTMLDivElement>(null);
  const addButton = useRef<HTMLButtonElement>(null);

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'social' });

  function focusRow(index: number) {
    queueMicrotask(() => {
      const rows = container.current?.children;
      const row = rows?.[index] as HTMLElement | undefined;
      const target = row?.querySelector<HTMLElement>('[data-repeater-remove]');
      (target ?? addButton.current)?.focus();
    });
  }

  function addRow() {
    append({ handle: '', url: '' });
    // Focus lands in the first control of the new row, not on the button that
    // made it — an addition that leaves focus behind reads as no addition.
    queueMicrotask(() => {
      const rows = container.current?.children;
      const row = rows?.[fields.length] as HTMLElement | undefined;
      row?.querySelector<HTMLInputElement>('input')?.focus();
    });
    announce(copy.handles.announce.added);
  }

  function removeRow(index: number) {
    const label = form.getValues(`social.${index}.handle`) ?? '';
    remove(index);
    // Focus moves before the node leaves; when the last row goes it lands on the
    // action that brings one back, which is also what the empty state offers.
    focusRow(Math.min(index, fields.length - 2));
    announce(copy.handles.announce.removed(label.length > 0 ? label : '', formatNumber(fields.length - 1)));
  }

  return (
    <div className="mt-6">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h3 className="mb-0 font-display text-h4 font-semibold">{copy.handles.heading}</h3>
        <button
          ref={addButton}
          type="button"
          onClick={addRow}
          className="hit-target ms-auto flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
        >
          <PlusIcon />
          <span>{copy.handles.add}</span>
        </button>
      </div>

      <p className="mb-4 mt-0 text-xs text-ink-muted">{copy.handles.note}</p>

      <div ref={container} className="grid gap-4">
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="grid gap-3 rounded-md border border-border bg-surface p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] md:items-end"
          >
            <div className="min-w-0">
              <label htmlFor={`${fieldPrefix}-${field.id}-label`} className="mb-2 block text-sm font-medium">
                {copy.handles.label}
              </label>
              <input
                {...form.register(`social.${index}.handle`)}
                id={`${fieldPrefix}-${field.id}-label`}
                type="text"
                className={CONTROL_CLASS}
              />
            </div>

            <div className="min-w-0">
              <label htmlFor={`${fieldPrefix}-${field.id}-url`} className="mb-2 block text-sm font-medium">
                {copy.handles.url}
              </label>
              <input
                {...form.register(`social.${index}.url`)}
                id={`${fieldPrefix}-${field.id}-url`}
                type="url"
                dir="ltr"
                aria-invalid={form.formState.errors.social?.[index]?.url ? true : undefined}
                className={IDENTIFIER_CLASS}
              />
              {form.formState.errors.social?.[index]?.url?.message ? (
                <p className="mb-0 mt-2 text-xs text-danger">
                  {form.formState.errors.social[index]?.url?.message}
                </p>
              ) : null}
            </div>

            {/*
              Full width at the base tier like every other action row, square
              again from the tablet tier where it stands in the line of two
              fields rather than under them.
            */}
            <button
              type="button"
              data-repeater-remove
              onClick={() => removeRow(index)}
              aria-label={copy.handles.remove(form.getValues(`social.${index}.handle`) ?? '')}
              className="hit-target flex w-full items-center justify-center rounded-sm text-danger hover:bg-danger-soft md:w-[2.75rem]"
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>

      {/*
        The empty state carries a reason in human language and the action that
        leaves it — which is the «أضف حساباً» directly above, so it names it
        rather than drawing a second button for one destination.
      */}
      {fields.length === 0 ? <p className="mb-0 mt-0 text-xs text-ink-muted">{copy.handles.empty}</p> : null}
    </div>
  );
}
